import {
	AdjustEnvConfig,
	type AdjustEventName,
	AdjustParameterKeys,
	type AdjustServiceConfig,
	type EventParams,
	isAdjustEventName,
	Logger,
	Service,
	type TrackingEventType,
	TrackingService
} from '@capsicum/common';

import { env } from '../../util/env';

import { isMarketingEnabled } from './utils';

let AdjustSdk: any;
let didInit = false;

function InitSDK(): void {
	const adjustAppEnvironment = AdjustEnvConfig[env.NEXT_PUBLIC_ENV || 'integration'];
	const isDev = adjustAppEnvironment.adjustEnv !== 'production';

	if (!didInit && typeof window !== 'undefined') {
		AdjustSdk = require('@adjustcom/adjust-web-sdk');
		AdjustSdk?.initSdk({
			appToken: adjustAppEnvironment.adjustAppToken,
			environment: isDev ? 'sandbox' : 'production',
			logLevel: isDev ? 'verbose' : 'error'
		});

		const referrer = getReferrer();

		if (referrer) {
			window.sessionStorage.setItem('adjust_referrer', referrer);
		} else {
			window.sessionStorage.removeItem('adjust_referrer');
		}

		didInit = true;
	}
}
function getReferrer(): string | undefined {
	// First, ensure `window` exists (e.g., in SSR, `window` may be undefined)
	if (typeof window === 'undefined') {
		return undefined;
	}

	return window.location?.search
		?.split('&')
		?.map(pair => pair.replace('?', '').split('='))
		?.find(item => item[0] === 'adjust_referrer')?.[1];
}
export class WebAdjustTracking extends TrackingService<EventParams, AdjustServiceConfig> {
	private TRACKED_PARAMETER_KEYS = ['user_id', 'service_fee', 'device_id'];

	protected override userId;

	public constructor(userId?: string) {
		super({});

		this.userId = userId;

		try {
			InitSDK();
		} catch (error) {
			Logger.error('Error initializing adjust sdk', error as Error);
		}
	}

	public async setUserId(userId: string) {
		this.userId = userId;
	}

	public sendEvent(eventType: TrackingEventType.track, eventName: AdjustEventName, params?: EventParams) {
		if (!isMarketingEnabled() || !eventName) {
			return;
		}
		if (!isAdjustEventName(eventName)) return;

		const revenue = Number(params?.revenue);

		const partnerParams: Array<any> = [];

		// Checks if customParameters is true and not empty
		if (params && Object.keys(params).length > 0) {
			Object.entries(params).forEach(([key, value]) => {
				if (this.TRACKED_PARAMETER_KEYS.includes(key)) partnerParams.push(key, value);
			});
		}

		this.userId && partnerParams.push(AdjustParameterKeys.USER_ID, this.userId);

		try {
			AdjustSdk?.trackEvent({
				currency: 'EUR',
				eventToken: AdjustEnvConfig[env.NEXT_PUBLIC_ENV || 'integration']?.[eventName],
				partnerParams,
				revenue
			});
		} catch (error) {
			Logger.error('Error sending adjust event', error as Error);
		}
	}

	public getParams(params: EventParams) {
		return params;
	}

	public get serviceId(): Service {
		return Service.WEBADJUST;
	}

	public setThirdPartySharing(isUserDataSharingAllowed: boolean, isRelevantAdsAllowed: boolean, isEea: boolean) {
		// DO NOTHING
	}
}
